import React, { useRef, useState } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  Typography,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import emissionViewStore from "../../Store/marco/emissionViewStore";

export const CustomSelector = observer(
  ({
    label,
    value,
    handleChange,
    options,
    placeholder,
    error,
    // 配置鍵
    valueKey = "value", // 實際儲存的值
    labelKey = "label", // 顯示的標籤
    disabled,
    showTooltip = false, // 是否要顯示提示
  }) => {
    const { t } = useTranslation("ActivityMain");
    const safeOptions = Array.isArray(options) ? options : [];
    const selectRef = useRef(null);
    const [menuPlacement, setMenuPlacement] = useState({
      opensUpward: false,
      maxHeight: 320,
    });

    const updateMenuPlacement = () => {
      const rect = selectRef.current?.getBoundingClientRect();
      if (!rect) return;

      const viewportHeight = window.innerHeight;
      const spaceAbove = rect.top - 8;
      const spaceBelow = viewportHeight - rect.bottom - 8;
      const opensUpward = spaceBelow < 240 && spaceAbove > spaceBelow;
      const availableSpace = opensUpward ? spaceAbove : spaceBelow;

      setMenuPlacement({
        opensUpward,
        maxHeight: Math.max(120, Math.min(320, availableSpace)),
      });
    };

    // 計算 tooltip 相關的邏輯
    const getTooltipConfig = () => {
      // 如果不需要顯示 tooltip，直接返回空配置
      if (!showTooltip) {
        return {
          shouldShow: false,
          title: "",
          disableHoverListener: true,
          showIcon: false,
        };
      }

      // 當數據已存在且不允許修改時
      if (!emissionViewStore.selectedDataEntryAllowModifyUnit) {
        return {
          shouldShow: true,
          title: t(
            "If data has already been added, the time format cannot be modified",
          ),
          disableHoverListener: false,
          showIcon: true,
        };
      }

      // 當選擇區間格式時
      if (value === 4) {
        return {
          shouldShow: true,
          title: t(
            "If user selects 【Interval】 as unit, the system will automatically allocate and calculate carbon emissions proportionally across the corresponding months based on the number of days in each month.",
          ),
          disableHoverListener: false,
          showIcon: true,
        };
      }

      // 其他情況不顯示 tooltip
      return {
        shouldShow: false,
        title: "",
        disableHoverListener: true,
        showIcon: false,
      };
    };

    const tooltipConfig = getTooltipConfig();

    return (
      <FormControl
        fullWidth
        sx={{ my: 1, minWidth: 80 }}
        error={error}
        disabled={disabled}
      >
        <Tooltip
          title={tooltipConfig.title}
          disableHoverListener={tooltipConfig.disableHoverListener}
        >
          <Typography sx={{ marginBottom: 1 }}>
            <span style={{ color: "red" }}>*</span> {label}
            {tooltipConfig.showIcon && (
              <InfoIcon
                color="primary"
                sx={{ fontSize: 18, marginLeft: 0.5, cursor: "pointer" }}
              />
            )}
          </Typography>
        </Tooltip>

        <Select
          ref={selectRef}
          value={value}
          onOpen={updateMenuPlacement}
          onChange={(e) => {
            // 找到完整的選項對象，以便傳遞給 handleChange
            const selectedOption = safeOptions.find(
              (option) => option[valueKey] === e.target.value,
            );
            handleChange(e, selectedOption);
          }}
          displayEmpty
          MenuProps={{
            disablePortal: false,
            marginThreshold: 8,
            anchorOrigin: {
              vertical: menuPlacement.opensUpward ? "top" : "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: menuPlacement.opensUpward ? "bottom" : "top",
              horizontal: "left",
            },
            PaperProps: {
              sx: { maxHeight: `${menuPlacement.maxHeight}px` },
            },
          }}
          renderValue={(selected) => {
            if (!selected) return <Typography>{placeholder}</Typography>;

            // 顯示標籤 (而非值)
            const selectedOption = safeOptions.find(
              (option) => option[valueKey] === selected,
            );
            return selectedOption ? t(selectedOption[labelKey]) : t("Unset");
          }}
        >
          {safeOptions.map((option) => (
            <MenuItem key={option[valueKey]} value={option[valueKey]}>
              {t(option[labelKey])}
            </MenuItem>
          ))}
        </Select>

        {error && (
          <FormHelperText>
            {t(
              valueKey === "unitId"
                ? "Please select a unit"
                : "Please select a time format",
            )}
          </FormHelperText>
        )}
      </FormControl>
    );
  },
);
